/**
 * 关于结算的工具类似
 */
import * as ClearingConstants from '../parameters/ClearingConstants'
import ColumnUtils from '@/utils/ColumnUtils'
import StrUtils from '@/utils/StrUtils'
import { getChargeForBillPayment, loadCharges } from '@/api/clearing/charge'
import { Assert } from '@/utils/validate'
import lang from '@/lang'
import * as RateUtil from '@/class/cargo/model/utils/RateUtil'
import * as NumberUtils from '@/utils/NumberUtils'
import {
  BILLING_CONFIRMED_STATE,
  BILLING_NOT_CONFIRMED_STATE,
  BILLING_REVIEWED_STATE
} from '@/class/cargo/model/parameters/BillingConstants'
import { cache, cacheName } from '@/utils/cacheManager'
import { queryBillingByGroupId, queryBillingsByChargeUuid } from '@/api/clearing/billingNative'
import $store from '@/store'

export class ClearingUtils {
  static SMALL_NUMBER = 0.001
  constructor() {
  }

  /**
   * 在查询时，有些条件不能在条语句里面返回，比较未销金额
   * 此函数用于是把其它的查询结果merge到实体中去。
   */
  static mergeWriteoffableResult(wapper) {
    const writeoffables = wapper.results
    const otherResult = wapper.otherResult
    if (otherResult && otherResult['writeoff']) {
      const amountMap = wapper.otherResult['writeoff']
      for (const writeoffable of writeoffables) {
        if (amountMap[writeoffable.uuid]) {
          const map = amountMap[writeoffable.uuid]
          const amount = map['amount']
          const verifiedAmount = map['verifiedAmount']
          const chargeCount = map['chargeCount']
          const paidOffCount = map['paidOffCount']
          let historyCount = 0
          // let confirmCount = 0
          let confirmHQCount = 0
          let verifiedStatus = 0
          let realVerifiedAmount = 0
          if (map.hasOwnProperty('historyCount')) { historyCount = map['historyCount'] }
          // if (map.hasOwnProperty('confirmCount')) { confirmCount = map['confirmCount'] }
          if (map.hasOwnProperty('confirmHQCount')) { confirmHQCount = map['confirmHQCount'] }
          if (map.hasOwnProperty('realVerifiedAmount')) { realVerifiedAmount = map['realVerifiedAmount'] }
          if (map.hasOwnProperty('verifiedStatus')) { verifiedStatus = map['verifiedStatus'] }

          const unverifiedAmount = amount - verifiedAmount

          writeoffable.verifiedAmount = verifiedAmount
          writeoffable.unverifiedAmount = unverifiedAmount
          // CARGO-6672，如果发票币种不是RMB，返回RMB金额值，页面端不在根据发票金额 * 汇率
          if (map.hasOwnProperty('amountRMB') && map.hasOwnProperty('verifiedAmountRMB')) {
            writeoffable.amount2RMB = map['amountRMB']
            writeoffable.verifiedAmount2RMB = map['verifiedAmountRMB']
            writeoffable.unverifiedAmount2RMB = map['amountRMB'] - map['verifiedAmountRMB']
          }

          // 配置参数(isClearHistory)，返回结果集中才有realVerifiedAmount字段
          if (map.hasOwnProperty('realVerifiedAmount')) {
            if (realVerifiedAmount === 0) {
              writeoffable.realClearingState = ClearingConstants.CLEARING_STATE_NON.code
              // CARGO-5251，0金额账单的：异反向相抵的 amount !=0、同方向相抵 amount =0！confirmHQCount =1 :分公司确认
              if (historyCount > 0) {
                if (confirmHQCount > 0) {
                  writeoffable.realClearingState = ClearingConstants.CLEARING_STATE_FULL.code
                } else {
                  writeoffable.realClearingState = ClearingConstants.CLEARING_STATE_TOBE_CONFIRMED.code
                }
              }
            } else if (amount === realVerifiedAmount) {
              if (confirmHQCount === historyCount) {
                if (Math.abs(verifiedAmount - amount) > this.SMALL_NUMBER || paidOffCount !== chargeCount) {
                  writeoffable.realClearingState = ClearingConstants.CLEARING_STATE_FULL_WARNING.code
                } else {
                  writeoffable.realClearingState = ClearingConstants.CLEARING_STATE_FULL.code
                }
              } else if (amount === verifiedAmount) {
                writeoffable.realClearingState = ClearingConstants.CLEARING_STATE_FULL.code
              } else {
                writeoffable.realClearingState = ClearingConstants.CLEARING_STATE_TOBE_CONFIRMED.code
              }
            }
          } else {
            // 包含就是付款申请页面查询
            if (map.hasOwnProperty('verifiedStatus')) {
              if (verifiedStatus === 1) {
                writeoffable.realClearingState = ClearingConstants.CLEARING_STATE_NON.code
              } else if (verifiedStatus === 2) {
                writeoffable.realClearingState = ClearingConstants.CLEARING_STATE_PARTIAL.code
              } else if (verifiedStatus === 3) {
                writeoffable.realClearingState = ClearingConstants.CLEARING_STATE_FULL.code
              } else {
                writeoffable.realClearingState = ClearingConstants.CLEARING_STATE_NON.code
              }
            } else {
              if (verifiedAmount === amount) {
                if (chargeCount === paidOffCount) {
                  writeoffable.realClearingState = ClearingConstants.CLEARING_STATE_FULL.code
                } else {
                  writeoffable.realClearingState = ClearingConstants.CLEARING_STATE_NON.code
                }
              } else if (verifiedAmount === 0) {
                writeoffable.realClearingState = ClearingConstants.CLEARING_STATE_NON.code
              } else {
                if (Math.abs(unverifiedAmount) > this.SMALL_NUMBER || Math.abs(unverifiedAmount) <= this.SMALL_NUMBER && chargeCount !== paidOffCount) {
                  writeoffable.realClearingState = ClearingConstants.CLEARING_STATE_PARTIAL.code
                } else if (Math.abs(unverifiedAmount) <= this.SMALL_NUMBER) {
                  writeoffable.realClearingState = ClearingConstants.CLEARING_STATE_FULL.code
                }
              }
            }
            // CARGO-4284 瀚峰：在【账单】页签中，部分核销显示未核销
            if (writeoffable.realClearingState === ClearingConstants.CLEARING_STATE_NON.code && verifiedAmount !== 0) {
              writeoffable.realClearingState = ClearingConstants.CLEARING_STATE_PARTIAL.code
            }
          }
        } else {
          writeoffable.verifiedAmount = 0
          writeoffable.realClearingState = 0
        }
      }
    }
  }

  /**
   * 合并结果集中有关开票信息的属性
   */
  static mergeInvoiceableResult(wapper) {
    const invoiceables = wapper.results
    const otherResult = wapper.otherResult
    if (otherResult && otherResult['invoice']) {
      const stateMap = otherResult['invoice']
      if (stateMap) {
        for (const invoiceable of invoiceables) {
          if (stateMap[invoiceable.uuid]) {
            const state = stateMap[invoiceable.uuid]
            const chargeCount = state['chargeCount']
            const invoiceCount = state['invoiceCount']
            invoiceable.uninvoiceAmount = state['uninvoiceAmount']
            if (invoiceCount === 0) { invoiceable.invoiceState = 0 } else { invoiceable.invoiceState = (chargeCount - invoiceCount) === 0 ? 2 : 1 }
          } else {
            invoiceable.invoiceState = 0
            invoiceable.uninvoiceAmount = 0
          }
        }
      }
    }
  }
  static mergeGroupBillingNos(wapper) {
    const billings = wapper.results
    const otherResult = wapper.otherResult
    const billingToBillingGroupObj = otherResult ? otherResult['billingGroup'] : null
    for (const billing of billings) {
      if (billingToBillingGroupObj === null) {
        billing.billingGroupNo = null
        continue
      }
      billing.billingGroupNo = billingToBillingGroupObj[billing.uuid]
    }
  }
  /**
   * 合并结果集中有关申请付款状态的属性
   */
  static mergePaymentStateResult(wapper) {
    const results = wapper.results
    const otherResult = wapper.otherResult
    if (otherResult && otherResult['billPayment']) {
      const stateMap = otherResult['billPayment']
      if (stateMap) {
        for (const item of results) {
          if (stateMap[item.uuid]) {
            const state = stateMap[item.uuid]
            const chargeCount = state['chargeCount']
            const paymentCount = state['paymentCount']
            if (paymentCount === 0) {
              item.paymentState = 0
            } else {
              item.paymentState = (chargeCount - paymentCount) === 0 ? 2 : 1
            }
          } else {
            item.paymentState = 0
          }
        }
      }
    }
  }
  // 合并凭证信息
  static mergeVoucherResult(wapper) {
    const list = wapper.results.list
    const otherResult = wapper.otherResult
    if (otherResult && otherResult['voucherDate']) {
      const map = otherResult['voucherDate']
      if (map) {
        for (const voucher of list) {
          if (map[voucher.uuid]) { voucher.voucherExportDate = ColumnUtils.dateLabelFunc(map[voucher.uuid]) } else { voucher.voucherExportDate = null }
        }
      }
    }
  }

  // 销账查询显示发票信息
  static mergeActualAmountInvoiceDetail(wapper) {
    const list = wapper.results.list
    const otherResult = wapper.otherResult
    if (otherResult && otherResult['invoice']) {
      //  ArrayCollection
      const invoiceDetail = otherResult['invoice']
      if (invoiceDetail) {
        for (const actualAmount of list) {
          actualAmount.invoiceDetail = null
        }
        for (const item of invoiceDetail) {
          for (const actualAmount of list) {
            if (item.actualAmountUuid === actualAmount.uuid) {
              if (StrUtils.isEmpty(actualAmount.invoiceDetail)) {
                actualAmount.invoiceDetail = this.getInvoiceDetail(item)
              } else {
                actualAmount.invoiceDetail += ('\n' + this.getInvoiceDetail(item))
              }
              break
            }
          }
        }
      }
    }
  }

  // 开票明细
  static getInvoiceDetail(item) {
    let str = ''
    if (!StrUtils.isEmpty(item.invoiceNo)) {
      str = (item.invoiceNo + ',')
    }
    if (!StrUtils.isEmpty(item.title)) {
      str += item.title
    }
    if (!StrUtils.isEmpty(item.invoiceDate)) {
      str += (',' + item.invoiceDate)
    }
    return str
  }

  static mergeCreditTerms(wapper) {
    const list = wapper.results.list
    const otherResult = wapper.otherResult
    if (otherResult && otherResult['creditTerm']) {
      const creditTermMap = otherResult['creditTerm']
      for (const actualAmount of list) {
        actualAmount.creditTerm = creditTermMap[actualAmount.uuid]
      }
    }
  }

  static mergeBillPayment(wapper) {
    const list = wapper.results.list
    const otherResult = wapper.otherResult
    if (otherResult && otherResult['billPayment']) {
      // ArrayCollection
      const billPaymentDetail = otherResult['billPayment']
      if (billPaymentDetail) {
        // ActualAmount
        let actual
        for (actual of list) {
          actual.billPaymentNo = null
        }
        for (const item of billPaymentDetail) {
          for (actual of list) {
            if (item.actualAmountUuid === actual.uuid) {
              actual.billPaymentNo = item.billPaymentNo
              break
            }
          }
        }
      }
    }
  }

  static mergeActualAmountTargetVoucherNoResult(wapper) {
    const list = wapper.results.list
    const otherResult = wapper.otherResult
    if (otherResult && otherResult['targetVoucherNo']) {
      const map = otherResult['targetVoucherNo']
      if (map) {
        for (const actualAmount of list) {
          if (map[actualAmount.uuid]) {
            actualAmount.targetVoucherNo2Charge = map[actualAmount.uuid]['targetVoucherNo']
            actualAmount.voucherDate2Charge = map[actualAmount.uuid]['voucherDate']
          } else {
            actualAmount.targetVoucherNo2Charge = ''
            actualAmount.voucherDate2Charge = null
          }
        }
      }
    }
  }

  // 原币汇差
  static mergeActualAmountCalcSourceRateDiffResult(wapper) {
    const list = wapper.results.list
    const otherResult = wapper.otherResult
    if (otherResult && otherResult['calcSourceRateDiff']) {
      const map = otherResult['calcSourceRateDiff']
      if (map) {
        for (const actualAmount of list) {
          if (map[actualAmount.uuid]) {
            actualAmount.sourceRateDiff = map[actualAmount.uuid]
          } else {
            actualAmount.sourceRateDiff = 0
          }
        }
      }
    }
  }

  /**
   * 是否是相同的结算单位
   */
  static isSameAccount(selections) {
    let same = true
    let accountId = -1
    for (const item of selections) {
      if (accountId === -1) { accountId = item.accountId } else if (accountId !== item.accountId) {
        same = false
        break
      }
    }
    return same
  }

  /**
   * 是否是相同的结算代理
   */
  static isSameSettlementAgent(selections) {
    let same = true
    let settlementAgentId = -1
    for (const item of selections) {
      if (settlementAgentId === -1) {
        settlementAgentId = item.settlementAgentId
      } else if (settlementAgentId !== item.settlementAgentId) {
        same = false
        break
      }
    }
    return same
  }

  /**
   * 是否是相同的开票抬头
   * invoiceTitle： 费用
   * title: 发票、账单、对账单
   * billPaymentInvoiceTitle： 付款申请
   */
  static isSameInvoiceTitle(selections, compareField) {
    let same = true
    let invoiceTitle
    for (const item of selections) {
      if (!invoiceTitle) {
        invoiceTitle = item[compareField]
      } else if (invoiceTitle !== item[compareField]) {
        same = false
        break
      }
    }
    return same
  }

  /**
   * 根据账单创建付款申请
   */
  static async createBillPaymentByBilling(billings = [], isAr = 0, requestSource = '') {
    const billingKeys = []
    for (const billing of billings) {
      billingKeys.push(billing.uuid)
    }
    const charges = await getChargeForBillPayment({ billingKeys: billingKeys, ar: isAr, requestSource, branchId: $store.getters.currentOfficeId }).then((res) => {
      return res.data
    }).finally()
    return await this.validateChargeForBillPayment(charges) ? charges : []
  }

  /**
   * 根据对账单创建付款申请
   */
  static async createBillPaymentByBillingGroup(billingGroups = [], isAr = 0) {
    const billingGroupKeys = []
    for (const billingGroup of billingGroups) {
      billingGroupKeys.push(billingGroup.uuid)
    }
    const charges = await getChargeForBillPayment({ billingGroupKeys: billingGroupKeys, ar: isAr, branchId: $store.getters.currentOfficeId }).then((res) => {
      return res.data
    }).finally()
    return await this.validateChargeForBillPayment(charges) ? charges : []
  }

  /**
   * 根据发票创建付款申请
   */
  static async createBillPaymentByInvoice(invoices = [], isAr = 0) {
    if (!ClearingUtils.isSameAccount(invoices)) {
      await Assert.isTrue(false, lang.t('CargoMsg.Cargo_SettlingPartyInconsistent'), 'warning')
      return []
    }
    const invoiceKeys = []
    for (const invoice of invoices) {
      invoiceKeys.push(invoice.uuid)
    }
    const charges = await getChargeForBillPayment({ invoiceKeys: invoiceKeys, ar: isAr, branchId: $store.getters.currentOfficeId }).then((res) => {
      return res.data
    }).finally()
    return await this.validateChargeForBillPayment(charges) ? charges : []
  }

  static async validateChargeForBillPayment(charges = []) {
    if (!charges || charges.length === 0) {
      await Assert.isTrue(false, lang.t('CargoMsg.Cargo_NoChargeForBillPayment'), 'warning')
      return false
    }
    let c1 = 0
    let c2 = 0
    for (const charge of charges) {
      const chargeAmount = isNaN(charge['chargeAmount']) ? 0.0 : charge['chargeAmount']
      // CARGO-19564 当对账单本身是应付，但里面的费用只有负数的应收时，点击付款申请会提示应收费用不能大于应付费用。
      const businessDate = !charge.chargeInputDate ? charge.chargeDateCreated : charge.chargeInputDate
      const localCurrency = RateUtil.localCurrencyByOfficeId(charge.chargeBranchId)
      if (charge['chargeReceivable']) {
        c1 += chargeAmount * RateUtil.getForeignExchangeRateSafely(localCurrency, charge['chargeCurrency'], businessDate, charge.chargeBranchId)
      } else {
        c2 += chargeAmount * RateUtil.getForeignExchangeRateSafely(localCurrency, charge['chargeCurrency'], businessDate, charge.chargeBranchId)
      }
    }
    if (!(c1 === 0 || c2 - c1 >= 0)) {
      await Assert.isTrue(false, lang.t('CargoMsg.Cargo_ARAmountCanTExceedAPAmount'), 'warning')
      return false
    }
    return true
  }
  static getBillState(state) {
    if (state < 3) {
      return lang.t('CargoMsg.Cargo_Uncommitted')
    }
    if (state === 4) {
      return lang.t('CargoMsg.Cargo_Submitted')
    }
    if (state === 5) {
      return lang.t('CargoMsg.Cargo_Received')
    }
    return ''
  }
  static numberPrecision(value) {
    if (isNaN(value)) { return '' }
    if (value < 0.01 && value > -0.01) { return '' }
    return StrUtils.numberFormatFunc(value, 2)
  }

  static getPaymentType(value) {
    if (value < -1 || value > 4) {
      return ''
    }
    const array = ClearingConstants.AMOUNT_RECEIVE_TYPE_LIST_OPTION
    return array[value + 1]
  }

  /**
   *
   * @param rowData 数据
   * @param maxAmount 最大金额
   * @param arField 收付类型字段
   * @param rateField 汇率字段
   * @param currencyField 币种字段
   * @param amountField 统计字段
   * @param makeInvoiceMethod 开票方式
   * @returns {Promise<{totalAmount: number, selectAll: boolean, selectedKeys: [], message: string}>}
   */
  static async autoCheckInvoicing(rowData = [], maxAmount = 0.0, arField = 'isReceivable', currencyField = 'currency', amountField = 'amount', rateField = '', makeInvoiceMethod, localCurrency) {
    const result = {
      selectAll: false, // 是否需要全部选中
      selectedKeys: [], // selectAll =false,记录需要被选中的uuid
      totalAmount: 0.00,
      message: ''
    }
    // 1.统计应收应付总金额
    let totalAr = 0.0
    let totalAp = 0.0
    for (const row of rowData) {
      const amount = isNaN(row[amountField]) ? 0 : row[amountField]
      let rate = 1
      if (rateField && row.hasOwnProperty(rateField)) {
        rate = isNaN(row[rateField]) ? 0.00 : row[rateField]
      } else {
        rate = RateUtil.getForeignExchangeRateSafely(localCurrency, row[currencyField], new Date(), 0)
      }
      if (row[arField]) {
        if (row[currencyField] === localCurrency) {
          totalAr = totalAr + row[amountField]
        } else {
          totalAr = totalAr + amount * rate
        }
      } else {
        if (row[currencyField] === localCurrency) {
          totalAp = totalAp + row[amountField]
        } else {
          totalAp = totalAp + amount * rate
        }
      }
    }
    if ((totalAr === 0 && totalAp === 0) || (totalAr + totalAp === 0)) {
      result.selectAll = true
      return result
    }
    // 2.根据应收和应付总金额都不等于0时，判断应收或者应付否需要被全部勾选并且计算出最后参与计算的总金额
    let lastTotalAmount = 0.00
    let selectAllAr = false
    let selectAllAp = false
    if (totalAr !== 0 && totalAp !== 0) {
      if (totalAr > 0 && totalAp > 0) {
        if (totalAr > totalAp) {
          selectAllAp = true
          lastTotalAmount = maxAmount + totalAp
        } else {
          selectAllAr = true
          lastTotalAmount = maxAmount + totalAr
          result.message = lang.t('Invoice.ArMoreApForAutoCheckInvoicing')
        }
      } else if (totalAr > 0 && totalAp < 0) {
        selectAllAp = true
        lastTotalAmount = maxAmount - totalAp
      } else if (totalAr < 0 && totalAp > 0) {
        selectAllAr = true
        lastTotalAmount = maxAmount - totalAr
        result.message = lang.t('Invoice.ArMoreApForAutoCheckInvoicing')
      } else {
        if (totalAr < totalAp) {
          selectAllAp = true
          lastTotalAmount = maxAmount - totalAp
        } else {
          selectAllAr = true
          lastTotalAmount = maxAmount - totalAr
        }
      }
      for (const c of rowData) {
        const isAr = c[arField]
        const amount = isNaN(c[amountField]) ? 0.00 : c[amountField]
        let rate = 1
        if (rateField && c.hasOwnProperty(rateField)) {
          rate = isNaN(c[rateField]) ? 0.00 : c[rateField]
        } else {
          rate = RateUtil.getForeignExchangeRateSafely(localCurrency, c[currencyField], new Date(), 0)
        }
        if ((isAr && selectAllAr) || (!isAr && selectAllAp)) { // 先处理掉应收或者应付的金额
          result.totalAmount = result.totalAmount - NumberUtils.roundHalfUp2(rate * amount)
        }
      }
    } else if (totalAr !== 0 && totalAp === 0) {
      lastTotalAmount = maxAmount
      let totalPositive = 0.0 // 正数总金额
      let totalNegative = 0.0 // 负数总金额
      for (const c of rowData) {
        const isAr = c[arField]
        if (isAr) {
          const amount = isNaN(c[amountField]) ? 0.00 : c[amountField]
          let rate = 1
          if (rateField && c.hasOwnProperty(rateField)) {
            rate = isNaN(c[rateField]) ? 0.00 : c[rateField]
          } else {
            rate = RateUtil.getForeignExchangeRateSafely(localCurrency, c[currencyField], new Date(), 0)
          }
          if (amount >= 0) {
            totalPositive = totalPositive + NumberUtils.roundHalfUp2(rate * amount)
          } else {
            totalNegative = totalNegative + NumberUtils.roundHalfUp2(rate * amount)
          }
        }
      }
      if (totalPositive < Math.abs(totalNegative)) {
        result.selectedKeys = []
        return result
      }
    } else if (totalAp !== 0 && totalAr === 0) {
      lastTotalAmount = maxAmount
      let totalPositive = 0.0 // 正数总金额
      let totalNegative = 0.0 // 负数总金额
      for (const c of rowData) {
        const isAr = c[arField]
        if (!isAr) {
          const amount = isNaN(c[amountField]) ? 0.00 : c[amountField]
          let rate = 1
          if (rateField && c.hasOwnProperty(rateField)) {
            rate = isNaN(c[rateField]) ? 0.00 : c[rateField]
          } else {
            rate = RateUtil.getForeignExchangeRateSafely(localCurrency, c[currencyField], new Date(), 0)
          }
          if (amount >= 0) {
            totalPositive = totalPositive + NumberUtils.roundHalfUp2(rate * amount)
          } else {
            totalNegative = totalNegative + NumberUtils.roundHalfUp2(rate * amount)
          }
        }
      }
      if (totalPositive < Math.abs(totalNegative)) {
        result.selectedKeys = []
        return result
      }
    }
    // 3.按照折合RMB金额从大到小排序
    const newList = this.sortListForAutoCheckInvoicing(rowData, amountField, currencyField, rateField, false, localCurrency)
    // 4.要根据应收应付总金额去找第一个小于0的坐标作为分割点
    let firstIndex = 0
    let index = 0
    for (const item of newList) {
      const isAr = item[arField]
      const amount = isNaN(item[amountField]) ? 0.00 : item[amountField]
      if (Math.abs(totalAr) > 0 && totalAp === 0) {
        if (isAr && amount < 0) {
          firstIndex = index
          break
        }
      } else if (Math.abs(totalAp) > 0 && totalAr === 0) {
        if (!isAr && amount < 0) {
          firstIndex = index
          break
        }
      } else {
        if (totalAr > totalAp) { // 应收大于应付
          if (isAr && amount < 0) {
            firstIndex = index
            break
          }
        } else { // 应付大于应收
          if (!isAr && amount < 0) {
            firstIndex = index
            break
          }
        }
      }
      index = index + 1
    }
    // 5.开始进行逻辑处理
    index = 0
    for (const c of newList) {
      const isAr = c[arField]
      if (Math.abs(totalAr) > 0 && totalAp === 0) {
        if (isAr && (firstIndex === 0 || (firstIndex > 0 && index < firstIndex))) { // 应收
          lastTotalAmount = await this.addSelectKeys(result, lastTotalAmount, c, amountField, rateField, currencyField, makeInvoiceMethod, localCurrency)
        }
      } else if (Math.abs(totalAp) > 0 && totalAr === 0) {
        if (!isAr && (firstIndex === 0 || (firstIndex > 0 && index < firstIndex))) { // 应付
          lastTotalAmount = await this.addSelectKeys(result, lastTotalAmount, c, amountField, rateField, currencyField, makeInvoiceMethod, localCurrency)
        }
      } else {
        if (totalAr > totalAp) { // 应收大于应付，默认选中所有应付
          if (isAr && (firstIndex === 0 || (firstIndex > 0 && index < firstIndex))) { // 应收
            lastTotalAmount = await this.addSelectKeys(result, lastTotalAmount, c, amountField, rateField, currencyField, makeInvoiceMethod, localCurrency)
          }
        } else { // 应付大于应收，默认选中所有应收
          if (!isAr && (firstIndex === 0 || (firstIndex > 0 && index < firstIndex))) { // 应付
            lastTotalAmount = await this.addSelectKeys(result, lastTotalAmount, c, amountField, rateField, currencyField, makeInvoiceMethod, localCurrency)
          }
        }
      }
      if (lastTotalAmount <= 0) {
        break
      }
      index = index + 1
    }
    // 6.既有应收又有应付时，补充默认勾选的应收或者应付数据
    if (result.selectedKeys.length > 0 && (selectAllAr || selectAllAp)) {
      for (const c of newList) {
        const isAr = c[arField]
        if ((isAr && selectAllAr) || (!isAr && selectAllAp)) {
          if (makeInvoiceMethod === 2) {
            result.selectedKeys.push(c)
          } else {
            result.selectedKeys.push(c.uuid)
          }
        }
      }
    }
    // 7.如果没有匹配到，则取输入数值的最接近的值。
    if (result.selectedKeys.length === 0) {
      let differenceAmount = 0.0
      let minUuid = ''
      let minAmount = 0.00
      for (const c of newList) {
        const amount = isNaN(c[amountField]) ? 0.00 : c[amountField]
        let rate = 1
        if (rateField && c.hasOwnProperty(rateField)) {
          rate = isNaN(c[rateField]) ? 0.00 : c[rateField]
        } else {
          rate = RateUtil.getForeignExchangeRateSafely(localCurrency, c[currencyField], new Date(), 0)
        }
        const amountAbs = Math.abs(NumberUtils.roundHalfUp2(amount * rate))
        if (amountAbs <= maxAmount) {
          // 取绝对值比较
          const a = Math.abs(NumberUtils.roundHalfUp2(maxAmount - amount * rate))
          if (differenceAmount === 0) {
            differenceAmount = a
            if (makeInvoiceMethod === 2) {
              minUuid = c
            } else {
              minUuid = c.uuid
            }
            minAmount = NumberUtils.roundHalfUp2(amount * rate)
          } else {
            if (a < differenceAmount) {
              differenceAmount = a
              if (makeInvoiceMethod === 2) {
                minUuid = c
              } else {
                minUuid = c.uuid
              }
              minAmount = NumberUtils.roundHalfUp2(amount * rate)
            }
          }
        }
      }
      if (minUuid) {
        result.selectedKeys.push(minUuid)
        result.totalAmount = minAmount
      }
    }
    if (result.selectedKeys.length === 0) {
      result.totalAmount = 0.00
    } else {
      result.totalAmount = NumberUtils.roundHalfUp2(result.totalAmount, 2)
    }
    return result
  }

  //
  static async addSelectKeys(result, balance, row, amountField, rateField, currencyField, makeInvoiceMethod, localCurrency) {
    const amount = isNaN(row[amountField]) ? 0.00 : row[amountField]
    let rate = 1
    if (rateField && row.hasOwnProperty(rateField)) {
      rate = isNaN(row[rateField]) ? 0.00 : row[rateField]
    } else {
      rate = RateUtil.getForeignExchangeRateSafely(localCurrency, row[currencyField], new Date(), 0)
    }
    if (row[currencyField] === localCurrency) {
      if (amount >= 0) {
        if (balance - amount >= 0) { // 余额大于0，继续减下去
          balance = NumberUtils.roundHalfUp2(balance - amount)
          result.totalAmount = result.totalAmount + amount
          if (makeInvoiceMethod === 2) {
            result.selectedKeys.push(row)
          } else {
            result.selectedKeys.push(row.uuid)
          }
        }
      } else {
        if (balance + amount >= 0) {
          balance = NumberUtils.roundHalfUp2(balance + amount)
          result.totalAmount = result.totalAmount + amount
          if (makeInvoiceMethod === 2) {
            result.selectedKeys.push(row)
          } else {
            result.selectedKeys.push(row.uuid)
          }
        }
      }
    } else { // 异币种
      if (amount >= 0) {
        if (balance - amount * rate >= 0) { // 余额大于0，继续减下去
          balance = NumberUtils.roundHalfUp2(balance - NumberUtils.roundHalfUp2(amount * rate, 2))
          if (makeInvoiceMethod === 2) {
            result.selectedKeys.push(row)
          } else {
            result.selectedKeys.push(row.uuid)
          }
          result.totalAmount = result.totalAmount + NumberUtils.roundHalfUp2(amount * rate, 2)
        }
      } else {
        if (balance + amount * rate >= 0) { // 余额大于0，继续减下去
          balance = NumberUtils.roundHalfUp2(balance + NumberUtils.roundHalfUp2(amount * rate, 2))
          if (makeInvoiceMethod === 2) {
            result.selectedKeys.push(row)
          } else {
            result.selectedKeys.push(row.uuid)
          }
          result.totalAmount = result.totalAmount + NumberUtils.roundHalfUp2(amount * rate, 2)
        }
      }
    }
    return NumberUtils.roundHalfUp2(balance, 2)
  }

  static sortListForAutoCheckInvoicing(list = [], amountField, currencyField, rateField, asc = false, localCurrency) {
    const newList = list.sort((a, b) => {
      let amountA = 0.00
      let amountB = 0.00
      if (a[currencyField] === localCurrency) {
        amountA = isNaN(a[amountField]) ? 0.00 : a[amountField]
      } else {
        let rate = 1
        if (rateField && a.hasOwnProperty(rateField)) {
          rate = isNaN(a[rateField]) ? 0.00 : a[rateField]
        } else {
          rate = RateUtil.getForeignExchangeRateSafely(localCurrency, a[currencyField], new Date(), 0)
        }
        amountA = NumberUtils.roundHalfUp2(a[amountField] * rate, 2)
      }
      if (b[currencyField] === localCurrency) {
        amountB = isNaN(b[amountField]) ? 0.00 : b[amountField]
      } else {
        let rate = 1
        if (rateField && b.hasOwnProperty(rateField)) {
          rate = isNaN(b[rateField]) ? 0.00 : b[rateField]
        } else {
          rate = RateUtil.getForeignExchangeRateSafely(localCurrency, b[currencyField], new Date(), 0)
        }
        amountB = NumberUtils.roundHalfUp2(b[amountField] * rate, 2)
      }
      return asc ? (amountA - amountB) : (amountB - amountA)
    })
    return newList
  }

  static autoCheckInvoicingForDetail(rowData = [], maxAmount, invoiceAr = false, makeInvoiceMethod = 0) {
    const result = {
      selectAll: false, // 是否需要全部选中
      selectedKeys: [], // selectAll =false,记录需要被选中的uuid
      message: ''
    }
    if (!rowData || rowData.length === 0 || !maxAmount) return result
    const charges = []
    for (const item of rowData) {
      if (makeInvoiceMethod === 1 || makeInvoiceMethod === 2) {
        charges.push(item)
      } else {
        if (item.entity !== undefined && item['entity'] === 'charge') {
          charges.push(item)
        }
      }
    }
    if (charges.length === 0) return result
    let totalAr = 0.0 // 应收总金额
    let totalAp = 0.0 // 应付总金额
    let totalPositiveForReceive = 0.0 // 应收正数总金额
    let totalNegativeForReceive = 0.0 // 应收负数总金额
    let totalPositiveForPayment = 0.0 // 应付正数总金额
    let totalNegativeForPayment = 0.0 // 应付负数总金额
    for (const charge of charges) {
      const invoiceAmount = charge.invoiceAmount2
      if (charge.receivable) {
        totalAr = totalAr + invoiceAmount
        if (invoiceAmount >= 0) {
          totalPositiveForReceive = totalPositiveForReceive + invoiceAmount
        } else {
          totalNegativeForReceive = totalNegativeForReceive + invoiceAmount
        }
      } else {
        totalAp = totalAp + invoiceAmount
        if (invoiceAmount >= 0) {
          totalPositiveForPayment = totalPositiveForPayment + invoiceAmount
        } else {
          totalNegativeForPayment = totalNegativeForPayment + invoiceAmount
        }
      }
    }
    // 如果只有应收的情况，负数金额大于正数金额，则不进行勾选;应付情况也是如此
    if ((Math.abs(totalAr) > 0 && totalAp === 0 && totalPositiveForReceive < Math.abs(totalNegativeForReceive)) ||
        (Math.abs(totalAp) > 0 && totalAr === 0 && totalPositiveForPayment < Math.abs(totalNegativeForPayment))) {
      result.selectedKeys = []
      return result
    }
    // 如果列表中应收应付费用都存在时
    let lastTotalAmount = 0.00
    let selectAllAr = false
    let selectAllAp = false
    if (totalAr !== 0 && totalAp !== 0) {
      if (totalAr > 0 && totalAp > 0) {
        if (totalAr > totalAp) {
          selectAllAp = true
          lastTotalAmount = maxAmount + totalAp
        } else {
          selectAllAr = true
          lastTotalAmount = maxAmount + totalAr
          result.message = lang.t('Invoice.ArMoreApForAutoCheckInvoicing')
        }
      } else if (totalAr > 0 && totalAp < 0) {
        selectAllAp = true
        lastTotalAmount = maxAmount - totalAp
      } else if (totalAr < 0 && totalAp > 0) {
        selectAllAr = true
        lastTotalAmount = maxAmount - totalAr
        result.message = lang.t('Invoice.ArMoreApForAutoCheckInvoicing')
      } else {
        if (totalAr < totalAp) {
          selectAllAp = true
          lastTotalAmount = maxAmount - totalAp
        } else {
          selectAllAr = true
          lastTotalAmount = maxAmount - totalAr
        }
      }
    } else if (totalAr !== 0 && totalAp === 0) {
      lastTotalAmount = maxAmount
      let totalPositive = 0.0 // 正数总金额
      let totalNegative = 0.0 // 负数总金额
      for (const c of charges) {
        const isAr = c.receivable
        if (isAr) {
          const amount = c.invoiceAmount2
          if (amount >= 0) {
            totalPositive = totalPositive + amount
          } else {
            totalNegative = totalNegative + amount
          }
        }
      }
      if (totalPositive < Math.abs(totalNegative)) {
        result.selectedKeys = []
        return result
      }
    } else if (totalAp !== 0 && totalAr === 0) {
      lastTotalAmount = maxAmount
      let totalPositive = 0.0 // 正数总金额
      let totalNegative = 0.0 // 负数总金额
      for (const c of charges) {
        const isAr = c.receivable
        if (!isAr) {
          const amount = c.invoiceAmount2
          if (amount >= 0) {
            totalPositive = totalPositive + amount
          } else {
            totalNegative = totalNegative + amount
          }
        }
      }
      if (totalPositive < Math.abs(totalNegative)) {
        result.selectedKeys = []
        return result
      }
    }
    const newChargeList = charges.sort((a, b) => {
      const amountA = isNaN(a.invoiceAmount2) ? 0.0 : Number(a.invoiceAmount2)
      const amountB = isNaN(b.invoiceAmount2) ? 0.0 : Number(b.invoiceAmount2)
      return amountB - amountA
    })
    // 根据发票收付类型，找对应费用的金额小于0的坐标
    let firstIndex = 0
    let index = 0
    for (const item of newChargeList) {
      const isAr = item.receivable
      const amount = isNaN(item.invoiceAmount2) ? 0.00 : item.invoiceAmount2
      if (Math.abs(totalAr) > 0 && totalAp === 0) {
        if (isAr && amount < 0) {
          firstIndex = index
          break
        }
      } else if (Math.abs(totalAp) > 0 && totalAr === 0) {
        if (!isAr && amount < 0) {
          firstIndex = index
          break
        }
      } else {
        if (totalAr > totalAp) { // 应收大于应付
          if (isAr && amount < 0) {
            firstIndex = index
            break
          }
        } else { // 应付大于应收
          if (!isAr && amount < 0) {
            firstIndex = index
            break
          }
        }
      }
      index = index + 1
    }
    // 处理勾选逻辑
    index = 0
    for (const c of newChargeList) {
      const isAr = c.receivable
      if (Math.abs(totalAr) > 0 && totalAp === 0) {
        if (isAr && (firstIndex === 0 || (firstIndex > 0 && index < firstIndex))) { // 应收
          lastTotalAmount = this.addSelectKeysForDetail(result, lastTotalAmount, c)
        }
      } else if (Math.abs(totalAp) > 0 && totalAr === 0) {
        if (!isAr && (firstIndex === 0 || (firstIndex > 0 && index < firstIndex))) { // 应付
          lastTotalAmount = this.addSelectKeysForDetail(result, lastTotalAmount, c)
        }
      } else {
        if (totalAr > totalAp) { // 应收大于应付，默认选中所有应付
          if (isAr && (firstIndex === 0 || (firstIndex > 0 && index < firstIndex))) { // 应收
            lastTotalAmount = this.addSelectKeysForDetail(result, lastTotalAmount, c)
          }
        } else { // 应付大于应收，默认选中所有应收
          if (!isAr && (firstIndex === 0 || (firstIndex > 0 && index < firstIndex))) { // 应付
            lastTotalAmount = this.addSelectKeysForDetail(result, lastTotalAmount, c)
          }
        }
      }
      if (lastTotalAmount <= 0) {
        break
      }
      index = index + 1
    }
    //
    if (result.selectedKeys.length > 0 && (selectAllAr || selectAllAp)) {
      for (const c of charges) {
        const isAr = c.receivable
        if ((isAr && selectAllAr) || (!isAr && selectAllAp)) {
          result.selectedKeys.push(c.uuid)
        }
      }
    }
    // 没有匹配上，取接近预设金额的值
    if (result.selectedKeys.length === 0) {
      let differenceAmount = 0.0
      let minUuid = ''
      for (const charge of newChargeList) {
        const invoiceAmount = charge.invoiceAmount2
        if (Math.abs(invoiceAmount) <= maxAmount) {
          // 取绝对值比较
          const a = Math.abs(NumberUtils.roundHalfUp2(maxAmount - invoiceAmount))
          if (differenceAmount === 0) {
            differenceAmount = a
            minUuid = charge.uuid
          } else {
            if (a < differenceAmount) {
              differenceAmount = a
              minUuid = charge.uuid
            }
          }
        }
      }
      if (minUuid) {
        result.selectedKeys.push(minUuid)
      }
    }
    return result
  }
  //
  static addSelectKeysForDetail(result, balance, row) {
    const amount = isNaN(row.invoiceAmount2) ? 0.00 : Number(row.invoiceAmount2)
    if (amount >= 0) {
      if (balance - amount >= 0) { // 余额大于0，继续减下去
        balance = NumberUtils.roundHalfUp2(balance - amount)
        result.selectedKeys.push(row.uuid)
      }
    } else {
      if (balance + amount >= 0) {
        balance = NumberUtils.roundHalfUp2(balance + amount)
        result.selectedKeys.push(row.uuid)
      }
    }
    return NumberUtils.roundHalfUp2(balance, 2)
  }

  /**
   * PILL-911 应付账单增加校验，需要在复核后才能付款申请
   * @param billingList
   * @returns {boolean}
   */
  static createBillPaymentCheckBillingState(billingList) {
    const billPaymentBillingState = cache.config.get('BillPaymentBillingState')
    if ((!billingList || billingList.length === 0) && StrUtils.isEmpty(billPaymentBillingState)) {
      return true
    }
    const billPaymentBillingStateList = billPaymentBillingState.split(',')
    let count = 0
    if (billPaymentBillingStateList.length === 1) {
      const billingState = billPaymentBillingStateList[0]
      for (const billing of billingList) {
        if (billingState === BILLING_NOT_CONFIRMED_STATE.code && !billing.confirmed) {
          count++
        } else if (billingState === BILLING_CONFIRMED_STATE.code && billing.confirmed) {
          count++
        } else if (billingState === BILLING_REVIEWED_STATE.code && billing.review) {
          count++
        } else {
          break
        }
      }
      if (count !== billingList.length) {
        Assert.isTrue(false, lang.t('CargoMsg.Cargo_BillStatusDoesNotMeetPaymentRequestRequirements'), 'warning')
        return false
      }
    } else if (billPaymentBillingStateList.length === 2) {
      if (billPaymentBillingStateList.includes(BILLING_NOT_CONFIRMED_STATE.code) && billPaymentBillingStateList.includes(BILLING_CONFIRMED_STATE.code)) {
        return true
      }
      for (const billing of billingList) {
        if (billPaymentBillingStateList.includes(BILLING_NOT_CONFIRMED_STATE.code) && billPaymentBillingStateList.includes(BILLING_REVIEWED_STATE.code) && !billing.confirmed && billing.review) {
          count++
        } else if (billPaymentBillingStateList.includes(BILLING_CONFIRMED_STATE.code) && billPaymentBillingStateList.includes(BILLING_REVIEWED_STATE.code) && billing.confirmed && billing.review) {
          count++
        } else {
          break
        }
      }
      if (count !== billingList.length) {
        Assert.isTrue(false, lang.t('CargoMsg.Cargo_BillStatusDoesNotMeetPaymentRequestRequirements'), 'warning')
        return false
      }
    }
    return true
  }

  /**
   * 对账单创建付款申请校验
   * @param billingGroups
   * @returns {Promise<boolean>}
   */
  static async checkCreateBillPaymentByBillingGroup(billingGroups) {
    if (billingGroups && billingGroups.length > 0) {
      const billingGroupKeys = []
      for (const billingGroup of billingGroups) {
        billingGroupKeys.push({ 'key': billingGroup.uuid, 'version': billingGroup.version })
      }
      const billings = await queryBillingByGroupId(billingGroupKeys).then(res => res.data)
      return this.createBillPaymentCheckBillingState(billings)
    }
    return true
  }

  /**
   * 费用创建付款申请校验
   * @param charges
   * @returns {Promise<boolean>}
   */
  static async checkCreateBillPaymentByCharges(charges) {
    if (!charges || charges.length === 0) return true
    const chargeKeys = []
    for (const charge of charges) {
      chargeKeys.push(charge.uuid)
    }
    return await this.checkCreateBillPaymentByChargeKeys(chargeKeys)
  }
  static async checkCreateBillPaymentByChargeKeys(chargeKeys) {
    if (!chargeKeys || chargeKeys.length === 0) return true
    const checkCharges = await loadCharges({ chargeKeys: chargeKeys }).then(res => { return res.data }).finally(() => {})
    const billings = await queryBillingsByChargeUuid(chargeKeys).then(res => { return res.data }).finally(() => {})
    if (checkCharges) {
      if (!billings || billings.length === 0) {
        checkCharges.forEach(item => {
          item.confirmed = false
          item.review = false
        })
      } else {
        for (const charge of checkCharges) {
          const billing = billings.find(item => item.uuid === charge.billingUuid)
          if (billing) {
            charge.confirmed = billing.confirmed
            charge.review = billing.review
          } else {
            charge.confirmed = false
            charge.review = false
          }
        }
      }
    }
    return this.createBillPaymentCheckBillingState(checkCharges)
  }

  /**
   * 获取付款申请界面自定义tab页签查询状态
   */
  static getUiCustomBillPaymentTabState(applicationStatus, billPaymentSetting, billPaymentRuleSetting, defaultState) {
    // 未配置UI定制或规则，直接返回默认
    if (!billPaymentSetting) {
      return defaultState
    }
    // 未开启UI定制或未配置主管审批，直接返回默认
    if (!billPaymentSetting.openUiCustom) {
      return defaultState
    }

    if (!billPaymentSetting.supervisorApproval || !billPaymentRuleSetting) {
      if (applicationStatus === 2) {
        if (billPaymentSetting.departmentApproval) {
          return ClearingConstants.BILL_PAYMENT_DEP_APROVED.code // 查询已部门审核状态
        }
      }
      return defaultState
    }

    // 前置审核节点 1:业务审核、2:部门审核、3:财务审核
    const preApproval = billPaymentRuleSetting.preApproval
    if (!preApproval) {
      return defaultState
    }
    switch (applicationStatus) {
      // 待财务审核页签
      case 2:
        if (billPaymentSetting.departmentApproval) { // 是否配置了部门页签
          if (preApproval === ClearingConstants.BILL_PAYMENT_PRE_STATE_DEPARTMENT_APPROVAL.code) {
            return ClearingConstants.BILL_PAYMENT_SUPERVISOR_APPROVED.code // 查询已主管审批状态
          } else {
            return ClearingConstants.BILL_PAYMENT_DEP_APROVED.code // 查询已部门审核状态
          }
        } else {
          if (preApproval === ClearingConstants.BILL_PAYMENT_PRE_STATE_BUSINESS_APPROVAL.code) {
            return ClearingConstants.BILL_PAYMENT_SUPERVISOR_APPROVED.code // 查询已主管审批状态
          }
        }
        break
        // 审核通过页签
      case 3:
        if (preApproval === ClearingConstants.BILL_PAYMENT_PRE_STATE_FINANCE_APPROVAL.code) {
          return ClearingConstants.BILL_PAYMENT_SUPERVISOR_APPROVED.code // 查询已主管审批状态
        }
        break
        // 待部门审核页签
      case 6:
        if (preApproval === ClearingConstants.BILL_PAYMENT_PRE_STATE_BUSINESS_APPROVAL.code) {
          return ClearingConstants.BILL_PAYMENT_SUPERVISOR_APPROVED.code // 查询已主管审批状态
        }
        break
        // 待主管审核页签
      case 11:
        if (preApproval === ClearingConstants.BILL_PAYMENT_PRE_STATE_BUSINESS_APPROVAL.code) {
          return ClearingConstants.BILL_PAYMENT_BIZ_APPROVED.code // 查询已业务审批状态
        } else if (preApproval === ClearingConstants.BILL_PAYMENT_PRE_STATE_DEPARTMENT_APPROVAL.code) {
          if (billPaymentSetting.departmentApproval) {
            return ClearingConstants.BILL_PAYMENT_DEP_APROVED.code // 查询已部门审批状态
          } else {
            return ClearingConstants.BILL_PAYMENT_BIZ_APPROVED.code // 查询已业务审批状态
          }
        } else if (preApproval === ClearingConstants.BILL_PAYMENT_PRE_STATE_FINANCE_APPROVAL.code) {
          return ClearingConstants.BILL_PAYMENT_FINANCE_APPROVED.code // 查询已财务审批状态
        }
        break
      default:
        break
    }
    return defaultState
  }
}
